"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EcommerceProductsProvider, useEcommerceProducts, ProductPrice } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { ArrowUpRight } from "lucide-react";
const wp_query_params = {
  per_page: 6,
  order: "desc",
  orderby: "date",
  tax_query: [{
    taxonomy: "product_cat",
    field: "term_id",
    terms: [16, 17]
  }]
};
const wp_query = new WP_Query(wp_query_params);
function getConditionBadge(productCategories: Array<{
  id: number;
  name: string;
}>) {
  const catNames = productCategories?.map(c => c.name.toLowerCase()) || [];
  if (catNames.some(n => n.includes("used"))) {
    return {
      label: "USED",
      isNew: false
    };
  }
  return {
    label: "NEW",
    isNew: true
  };
}
function FeaturedProductsConsumer() {
  const {
    products,
    loading,
    error,
    refetch
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[0, 1, 2].map(i => <div key={i} data-index={i} className="border border-border bg-card p-4 space-y-4 animate-pulse rounded-none">
            <div className="aspect-[16/10] bg-muted" />
            <div className="h-6 bg-muted w-3/4" />
            <div className="h-16 bg-muted w-full" />
            <div className="h-10 bg-muted w-full" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="border border-border bg-muted p-8 text-center rounded-none">
        <p className="font-mono text-sm text-muted-foreground mb-4">
          Unable to synchronize machinery inventory from catalog database.
        </p>
        <Button variant="outline" onClick={() => refetch()} className="rounded-none font-mono text-xs uppercase tracking-wider">
          Retry Connection
        </Button>
      </div>;
  }
  if (!products || products.length === 0) {
    return <div className="border border-border bg-muted p-12 text-center rounded-none">
        <p className="font-mono text-sm text-muted-foreground">
          No commercial equipment units currently flagged as featured. Check full inventory listings.
        </p>
        <div className="mt-4">
          <Button asChild variant="outline" className="rounded-none uppercase tracking-wider">
            <Link to="/ShopPage">View All Equipment</Link>
          </Button>
        </div>
      </div>;
  }
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {products.map((product, i) => {
      const condition = getConditionBadge(product.categories);
      const productReference = product.customFields?.sku || product.id;
      const availabilityStatus = product.is_in_stock === false ? "Out of Stock" : "In Stock";
      return <article key={product.id} data-index={i} className="group flex flex-col bg-card border border-border rounded-none transition-[border-color] duration-180 hover:border-foreground">
            {/* Photographic Area */}
            <div className="relative aspect-[16/10] w-full overflow-hidden bg-muted border-b border-border p-4">
              {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} loading="lazy" className="h-full w-full object-contain object-center" /> : <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground font-mono text-xs">
                  NO PHOTOGRAPHIC LOG AVAILABLE
                </div>}

              {/* Status Badge */}
              <div className="absolute top-3 left-3">
                <Badge variant={condition.isNew ? "secondary" : "outline"} className="rounded-none font-mono uppercase tracking-widest text-[11px] px-2.5 py-1 bg-foreground text-background border-transparent">
                  {condition.label}
                </Badge>
              </div>
            </div>

            {/* Content Plate */}
            <div className="p-6 flex flex-col flex-1 justify-between gap-5">
              <div>
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-serif text-2xl font-bold uppercase tracking-wide text-foreground line-clamp-2">
                    {product.title}
                  </h3>
                </div>

                {/* Price Display */}
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="font-serif text-2xl font-bold tracking-tight text-foreground">
                    <ProductPrice product={product} />
                  </span>
                </div>

                {/* Product Reference & Availability Spec Plate */}
                <div className="mt-4 border border-border bg-muted/50 p-3 space-y-2 rounded-none">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-mono text-muted-foreground uppercase">
                      Product Reference
                    </span>
                    <span className="font-mono font-medium text-foreground text-right truncate max-w-[170px]">
                      {productReference}
                    </span>
                  </div>
                  <div className="border-t border-border/60 pt-2 flex items-center justify-between text-xs">
                    <span className="font-mono text-muted-foreground uppercase">
                      Availability
                    </span>
                    <span className="font-mono font-medium text-foreground text-right truncate max-w-[170px]">
                      {availabilityStatus}
                    </span>
                  </div>
                </div>
              </div>

              {/* Dual Action Buttons */}
              <div className="flex flex-col sm:flex-row items-stretch gap-2.5 pt-2 border-t border-border">
                <Button asChild variant="outline" className="flex-1 rounded-none uppercase font-serif text-sm font-semibold tracking-wider border-foreground hover:bg-foreground hover:text-background h-11">
                  <Link to={product.link}>
                    View Details
                  </Link>
                </Button>
                <Button asChild className="flex-1 rounded-none uppercase font-serif text-sm font-semibold tracking-wider bg-primary text-primary-foreground hover:bg-primary/90 h-11">
                  <Link to="/RequestQuotePage">
                    Enquire About This Bull
                  </Link>
                </Button>
              </div>
            </div>
          </article>;
    })}
    </div>;
}
export default function FeaturedProducts() {
  return <section id="featured-woocommerce-products" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 border-b border-border">
      <div className="max-w-[80rem] mx-auto px-5 sm:px-10 lg:px-16">
        {/* Header Block */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-12 border-b border-border mb-12">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-3">
              <span className="h-2 w-2 bg-primary inline-block" />
              <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                Inventory Catalog
              </p>
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl font-bold uppercase tracking-tight text-foreground">Featured Mechanical Bulls for Sale</h2>
            <p className="mt-3 text-base text-muted-foreground max-w-xl">Finding a mechanical bull for sale should not mean scrolling through endless sales copy before you can see the equipment. Our featured inventory lets you compare current machines by price, condition, availability, important specifications and included equipment. From there, you can open each product page, review the available details and send an enquiry for the exact machine you are considering.</p>
          </div>

          <div className="flex items-center gap-4">
            <Button asChild variant="outline" className="rounded-none uppercase font-serif text-sm font-semibold tracking-wider border-border hover:border-foreground">
              <Link to="/ShopPage" className="inline-flex items-center gap-2">
                All Inventory
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Dynamic WooCommerce Catalog */}
        <EcommerceProductsProvider wp_query={{
        per_page: 6,
        order: "desc",
        orderby: "date",
        tax_query: [{
          taxonomy: "product_cat",
          field: "term_id",
          terms: [16, 17]
        }]
      }}>
          <FeaturedProductsConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>;
}